import React from 'react';
import { Link } from 'react-router-dom';
import { css, StyleSheet } from 'aphrodite';
import { Tooltip } from '@material-ui/core';
import moment from 'moment';

import { BaseEntityPage } from '../../common/components/BaseEntityPage';
import { DateRangePicker } from '../../common/components/charts/DateRangePicker';
import { Helper } from '../../common/helpers/Helper';
import { ApiHelper } from '../../common/helpers/ApiHelper';
import { Toast } from '../../components/Toast';
import { AppStyles } from '../../styles/AppTheme';
import { TableComponent } from '../../common/components/TableComponent';

const MAX_DATE_RANGE_DAYS = 20;

export class ExchangeCreativesReviewQueue extends BaseEntityPage {
  constructor(props, options) {
    super(props, options);

    const dateEnd = moment().format('YYYY-MM-DD');
    const dateStart = moment().subtract(MAX_DATE_RANGE_DAYS - 1, 'days').format('YYYY-MM-DD');

    this.pageKey = 'ExchangeCreativesReviewQueue';
    this.pageTitle = 'Exchange Creatives Review Queue';
    this.noAdd = true;
    this.noTableHead = true
    this.breadCrumbs = [
      { title: Helper.getString('home'), to: this.baseRoute() },
      { title: this.pageTitle },
    ];
    this.tableHeadersMap = {
      Date: (current) => (
        <span className={css(Styles.dateValue)}>
          {/* {moment(current.date, 'YYYY-MM-DD').format('Do MMM YYYY')} */}
          {Helper.formatDate3({ date: current.date, inputFormat: 'YYYY-MM-DD', outputFormat: 'MMMM Do, YYYY', suffix: ' (CST)' })}
        </span>
      ),
      'Pending Review': (current) => (
        <span className={css(Styles.reviewPending)}>
          <span className={css(Styles.pendingCount)}>{current.reviewPending}</span>
          <span className={css(Styles.pendingLabel)}> Pending Review</span>
        </span>
      ),
    };
    this.tableHeaders = Object.keys(this.tableHeadersMap);
    this.emptyText = 'No review-pending creatives found for this date range';
    this.state = {
      ...this.state,
      fetchState: ApiHelper.State.READY,
      dateStart,
      dateEnd,
      items: [],
      showToast: false,
      toastMsg: '',
    };
    this.toFetch = this.makeFetches(dateStart, dateEnd);
  }

  makeFetches = (dateStart, dateEnd) => [{
    method: 'GET',
    paths: ['miscs', 'creative-review-counts'],
    queryParams: {
      startPeriod: dateStart.replaceAll('-', ''),
      endPeriod: dateEnd.replaceAll('-', ''),
    },
  }]

  onDateChange = ({ dateStart, dateEnd }) => {
    this.setState({ dateStart, dateEnd });
  }

  onFilter = ({ dateStart, dateEnd }) => {
    const start = moment(dateStart, 'YYYY-MM-DD', true);
    const end = moment(dateEnd, 'YYYY-MM-DD', true);

    if (!start.isValid() || !end.isValid()) {
      this.showFilterError('Select a valid start date and end date');
      return;
    }
    if (end.isBefore(start)) {
      this.showFilterError('End date must be on or after the start date');
      return;
    }
    if (end.diff(start, 'days') + 1 > MAX_DATE_RANGE_DAYS) {
      this.showFilterError(`The maximum allowed date range is ${MAX_DATE_RANGE_DAYS} days`);
      return;
    }
    if (end.isAfter(moment(), 'day')) {
      this.showFilterError('End date cannot be after the current date');
      return;
    }

    this.toFetch = this.makeFetches(dateStart, dateEnd);
    this.setState({
      dateStart,
      dateEnd,
      items: [],
      showToast: false,
    }, () => this.fetchItems());
  }

  showFilterError = (toastMsg) => {
    this.setState({ showToast: true, toastMsg });
  }

  renderAboveTable = () => (
    <div className={css(Styles.filterWrap)}>
      <DateRangePicker
        dateStart={this.state.dateStart}
        dateEnd={this.state.dateEnd}
        buttonLabel='Search'
        onDateChange={this.onDateChange}
        onFilter={this.onFilter}
      />
      <Toast
        open={this.state.showToast}
        severity='error'
        message={this.state.toastMsg}
        handleClose={() => this.setState({ showToast: false })}
      />
    </div>
  )

  renderTable() {
    if (!this.onTableValue) {
      return ''
    }
    return (
      <TableComponent
        tableHeaders={this.tableHeaders}
        noTableHead={this.noTableHead || false}
        onTableValue={this.onTableValue ? this.onTableValue.bind(this) : this.onTableValue}
        tableHeadersMap={this.tableHeadersMap}
        items={this.state.items}
        allItems={this.state.allItems || this.state.items}
        setItems={this.setItems}
        headerActions={this.headerActions} //Multi checkbox
        onActions={this.onActions.bind(this)}
        onMenuItems={this.onMenuItems && this.onMenuItems.bind(this)}
        nextPage={this.state.nextPage}
        nextPageState={this.state.nextPageState}
        onNext={this.onNext.bind(this)}
        emptyText={this.emptyText}
        cellWidths={this.cellWidths}
        displayCheckBoxTable={this.displayCheckBoxTable || false}
        checkBoxActions={this.checkBoxActions.bind(this)}
        bodyCellStyle={Styles.bodyCell}
      />
    )
  }

  onTableValue(current, index) {
    if (!this.tableHeaders[index]) return '';
    return this.tableHeadersMap[this.tableHeaders[index]](current);
  }

  drillDownButton = (current) => (
    <Link
      key={`${current.date}-pending-creatives`}
      target='_blank'
      to={`/admin/exchange-creatives?tag=_MOD&day=${encodeURIComponent(current.date.replaceAll('-', ''))}&mode=review-queue`}
    >
      <Tooltip title='Review pending creatives' placement='top-start'>
        <span className={css(AppStyles.entityTableLinkIcon)}>
          <i className='material-icons-outlined'>playlist_play</i>
        </span>
      </Tooltip>
    </Link>
  )

  onActions(current) {
    return [this.drillDownButton(current)];
  }
}

const Styles = StyleSheet.create({
  filterWrap: {
    width: '100%',
  },
  dateValue: {
    fontSize: 16,
  },
  reviewPending: {
    color: '#ff9c55',
    fontWeight: '600 !important',
    display: 'flex',
    flexWrap: 'nowrap',
    alignItems: 'center',
    gap: 13,
    backgroundColor: '#ff9c551a',
    borderRadius: 10,
    maxWidth: 250
  },
  pendingCount: {
    fontSize: 30,
    padding: '0px 5px',
    minWidth: 70,
    textAlign: 'center'
  },
  pendingLabel: {
    fontSize: 16,
    flex: 1,
    textAlign: 'center'
  },
  bodyCell: {
    padding: '10px !important'
  }
});



// WEBPACK FOOTER //
// ./src/screens/admin/ExchangeCreativesReviewQueue.js